import {
  isSameOriginApplicationResource,
  shouldShowStartupFallbackForWindowError,
} from './startupErrorHandling';
import { isDynamicModuleFetchFailure } from './startupModuleLoading';

// Global error handler for uncaught errors (mobile-friendly)
export function showFallbackError(error: Error | string): void {
  const rootElement = document.getElementById('root');
  if (rootElement) {
    rootElement.innerHTML = `
      <div data-rudi-startup-fallback style="
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: #fff;
      ">
        <div style="max-width: 500px; text-align: center;">
          <div style="font-size: 48px; margin-bottom: 16px;">⚠️</div>
          <h1 style="font-size: 24px; margin-bottom: 16px; color: #333;">
            Failed to Load Application
          </h1>
          <p style="color: #666; margin-bottom: 24px; line-height: 1.5;">
            The application failed to initialize. This may be due to browser compatibility issues.
          </p>
          <details style="
            text-align: left;
            background: #f5f5f5;
            padding: 16px;
            border-radius: 8px;
            margin-bottom: 24px;
          ">
            <summary style="cursor: pointer; font-weight: 600; margin-bottom: 8px;">
              Error Details
            </summary>
            <pre style="
              overflow-x: auto;
              font-size: 12px;
              margin: 0;
              white-space: pre-wrap;
              word-wrap: break-word;
            ">${typeof error === 'string' ? error : error.message}\n\n${typeof error !== 'string' && error.stack ? error.stack : ''}</pre>
          </details>
          <button onclick="window.location.reload()" style="
            background: #2563eb;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            font-size: 16px;
            cursor: pointer;
            font-weight: 500;
          ">
            Reload Page
          </button>
        </div>
      </div>
    `;
  }
  console.error('[Rudi] Fatal initialization error:', error);
}

// Stale-bundle recovery: after a deploy, an open tab may reference hashed
// asset/chunk files that have since been replaced and now 404. A single full
// reload pulls the fresh index.html + chunk hashes. Throttled to once / 60s
// (shared `rudi_stale_reload` guard) so a persistently-failing load can't
// hard-loop the page instead of falling through to the error UI. Returns true
// if a reload was triggered.
function reloadOnceForStaleBundle(): boolean {
  const lastReload = sessionStorage.getItem('rudi_stale_reload');
  const now = Date.now();
  if (!lastReload || now - parseInt(lastReload) > 60000) {
    sessionStorage.setItem('rudi_stale_reload', String(now));
    window.location.reload();
    return true;
  }
  return false;
}

// Global error handlers
window.onerror = (message, source, lineno, colno, error) => {
  // rrweb-player intentionally renders the recorded DOM in <iframe sandbox>
  // without `allow-scripts` to prevent the recorded app's JS from re-firing
  // (re-running API calls, mutating data) on playback. Browsers fire a
  // top-level error when the recorded DOM contains <script>. This is expected,
  // not actionable.
  const msgStr = typeof message === 'string' ? message : String(message ?? '');
  if (msgStr.includes("frame is sandboxed and the 'allow-scripts' permission is not set")) {
    return true;
  }
  console.error('[Rudi] Global error:', { message, source, lineno, colno, error });
  if (!shouldShowStartupFallbackForWindowError({ message, source, error })) {
    return true;
  }
  // NOTE: We deliberately do NOT reload here just because `source` points at an
  // /assets/ file. In a production build *all* app code is bundled under
  // /assets/*.js, so that match also catches ordinary runtime exceptions (e.g.
  // a TipTap/ProseMirror plugin throwing while a peer's collab cursors render),
  // turning a recoverable component error into a full-page hard refresh. Genuine
  // stale-bundle recovery is handled by the precise signals below: the capture-
  // phase resource-load listener (failed <script>/<link>), the dynamic-import
  // message match in `unhandledrejection`, and `vite:preloadError`. Everything
  // else falls through to the React ErrorBoundary.
  // Only show fallback if React hasn't rendered yet
  const rootElement = document.getElementById('root');
  if (rootElement && !rootElement.hasChildNodes()) {
    showFallbackError(error || new Error(String(message)));
  }
  return true; // Prevent default error handling
};

window.addEventListener('unhandledrejection', event => {
  console.error('[Rudi] Unhandled promise rejection:', event.reason);
  // Vite HMR errors are non-fatal development issues. Keep the app running.
  const stack = event.reason?.stack || '';
  if (stack.includes('@vite/client') || stack.includes('node_modules/vite')) {
    event.preventDefault();
    return;
  }
  // Auto-reload once if a dynamic import failed. Stale bundles after a
  // deploy are the usual cause; Chrome's TypeError mentions the asset URL
  // ("Failed to fetch dynamically imported module: …/assets/…"), Safari just
  // says "Importing a module script failed" with no URL. Match only these
  // dynamic-import message signatures,
  // not `stack.includes('/assets/')`: in a production build virtually every
  // rejected promise originates from an async function whose stack lives in an
  // /assets/ chunk, so that broader check would reload on ordinary rejected
  // promises (failed fetches, etc.). Without this fall-through, Safari users
  // get a permanently-broken React tree (a lazy view throws every render)
  // until they manually clear cache.
  if (isDynamicModuleFetchFailure(event.reason) && reloadOnceForStaleBundle()) {
    return;
  }
  // Only show fallback if React hasn't rendered yet
  const rootElement = document.getElementById('root');
  if (rootElement && !rootElement.hasChildNodes()) {
    showFallbackError(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    );
  }
  event.preventDefault(); // Prevent default error handling
});

// Auto-reload when Vite chunk preload fails (stale deploy cache).
// Throttle to once per 60s (same guard as window.onerror / unhandledrejection
// above). Without it, a persistent preload failure, such as a stale index.html
// that references missing chunk hashes, reloads instantly
// and forever, hard-looping the page instead of falling through to the error UI.
window.addEventListener('vite:preloadError', () => {
  if (reloadOnceForStaleBundle()) {
    return;
  }
  // Already reloaded recently and the chunk still fails to load. Do NOT call
  // event.preventDefault() here: in Vite's preload helper, preventDefault
  // SUPPRESSES the rethrow, so the failed dynamic import resolves to `undefined`
  // instead of rejecting. React.lazy then reads `.default` of that `undefined`
  // module and throws a cryptic "Cannot read properties of undefined (reading
  // 'default')" that masks the real chunk-load failure (this is what users hit
  // when opening a lazy view from a tab held open across a deploy). Leaving the
  // event un-prevented lets Vite rethrow the actual error, so React's error
  // boundary surfaces a meaningful failure instead of the misleading crash.
});

// True same-origin asset-load failures occur when a hashed <script> or <link>
// returns 404 after a deploy. They fire a non-bubbling `error` event on the
// element itself, which `window.onerror` never sees. We catch it in the capture
// phase and reload once. Third-party scripts are optional to Rudi startup and
// must not trigger recovery when DNS, privacy software, or a content policy
// blocks them. Unlike the old
// `source.includes('/assets/')` check in `window.onerror` (removed above), this
// matches ONLY genuine resource fetch failures, never ordinary runtime
// exceptions, so the auto-reload is safe.
window.addEventListener(
  'error',
  event => {
    if (isSameOriginApplicationResource(event.target)) {
      reloadOnceForStaleBundle();
    }
  },
  true
);
